import { startOfWeek } from 'date-fns';
import { intToDate } from '@/helpers/dateTime';

/**
 * Build Highcharts `xAxis.plotLines` configs that mark skipped time buckets in a
 * dashboard chart's categories: a dashed vertical with a "+Nh" / "+Nmin" / "+Nd"
 * / "+Nw" / "+Nmo" label sits between consecutive bars whenever they skip over
 * empty buckets.
 *
 * Without these labels an hourly view like 06:00–10:00 then 15:00–17:30 renders
 * as evenly-spaced bars and misleads the reader. Same visual the Shuttle
 * RidershipByRouteChart uses so on-demand dashboard charts match it.
 *
 * Supported format types (from chartXAxisFormatter.getXAxisFormatType):
 *   - 'hourly':  `item.hour` is HHMM-packed (830 → 08:30, 1500 → 15:00)
 *   - 'daily':   `item.operationDay` is YYYYMMDD
 *   - 'weekly':  `item.operationDay` is any day within the aggregated week
 *   - 'monthly': `item.operationDay` is any day within the aggregated month
 * 'weekday' returns [] — the categorical 7-slot axis has no "skipped day" sense.
 */
export default function buildGapPlotLines(items, formatType) {
  if (!items || items.length < 2) return [];

  const makeLine = (index, text) => ({
    value: index - 0.5,
    color: '#ccd3dc',
    width: 1,
    dashStyle: 'Dash',
    label: {
      text,
      style: { fontSize: '10px', color: '#8896ab' },
      verticalAlign: 'middle',
      textAlign: 'center',
    },
  });
  const lines = [];

  if (formatType === 'hourly') {
    const toMinutes = (hourInt) => {
      if (hourInt == null) return null;
      const h = Math.floor(hourInt / 100);
      const m = hourInt % 100;
      return h * 60 + m;
    };
    // Hourly items cover one operation window in window order (sortHourlyItemsByWindow), so a
    // step that goes backwards wraps past midnight: 23:00 → 00:00 is 60 minutes, 22:00 → 01:00 is 180.
    const MINUTES_PER_DAY = 24 * 60;
    const stepMinutes = (prevHour, currHour) => {
      const prev = toMinutes(prevHour);
      const curr = toMinutes(currHour);
      if (prev == null || curr == null) return null;
      return curr >= prev ? curr - prev : curr - prev + MINUTES_PER_DAY;
    };
    // Infer interval from the smallest consecutive step so 15/30/60-min data all work;
    // fall back to 60 if we can't tell.
    let intervalMinutes = Infinity;
    for (let i = 1; i < items.length; i += 1) {
      const step = stepMinutes(items[i - 1].hour, items[i].hour);
      if (step > 0 && step < intervalMinutes) intervalMinutes = step;
    }
    if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) intervalMinutes = 60;

    for (let i = 1; i < items.length; i += 1) {
      const step = stepMinutes(items[i - 1].hour, items[i].hour);
      if (step != null) {
        const gap = step - intervalMinutes;
        // One skipped bucket already counts: 15:00 → 17:00 at a 60-min interval leaves
        // gap = 60, which is one missing hour and should render "+1h".
        if (gap >= intervalMinutes) {
          const gapHours = Math.floor(gap / 60);
          const gapMins = gap % 60;
          const text = gapHours ? `+${gapHours}h${gapMins ? ` ${gapMins}min` : ''}` : `+${gapMins}min`;
          lines.push(makeLine(i, text));
        }
      }
    }
  } else if (formatType === 'daily') {
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    for (let i = 1; i < items.length; i += 1) {
      if (items[i - 1]?.operationDay != null && items[i]?.operationDay != null) {
        const prevDate = intToDate(items[i - 1].operationDay);
        const currDate = intToDate(items[i].operationDay);
        const diffDays = Math.round((currDate - prevDate) / MS_PER_DAY);
        const gapDays = diffDays - 1;
        if (gapDays >= 1) {
          lines.push(makeLine(i, `+${gapDays}d`));
        }
      }
    }
  } else if (formatType === 'weekly') {
    // Aggregated buckets carry the first row's operationDay, which can be any weekday —
    // normalize both sides to the ISO week start before counting weeks between them.
    const MS_PER_WEEK = 1000 * 60 * 60 * 24 * 7;
    for (let i = 1; i < items.length; i += 1) {
      if (items[i - 1]?.operationDay != null && items[i]?.operationDay != null) {
        const prevWeekStart = startOfWeek(intToDate(items[i - 1].operationDay), { weekStartsOn: 1 });
        const currWeekStart = startOfWeek(intToDate(items[i].operationDay), { weekStartsOn: 1 });
        const diffWeeks = Math.round((currWeekStart - prevWeekStart) / MS_PER_WEEK);
        const gapWeeks = diffWeeks - 1;
        if (gapWeeks >= 1) {
          lines.push(makeLine(i, `+${gapWeeks}w`));
        }
      }
    }
  } else if (formatType === 'monthly') {
    for (let i = 1; i < items.length; i += 1) {
      if (items[i - 1]?.operationDay != null && items[i]?.operationDay != null) {
        const prevDate = intToDate(items[i - 1].operationDay);
        const currDate = intToDate(items[i].operationDay);
        const diffMonths =
          (currDate.getFullYear() - prevDate.getFullYear()) * 12 + (currDate.getMonth() - prevDate.getMonth());
        const gapMonths = diffMonths - 1;
        if (gapMonths >= 1) {
          lines.push(makeLine(i, `+${gapMonths}mo`));
        }
      }
    }
  }

  return lines;
}
